import * as PIXI from 'pixi.js';
import { setStrokeStyle, strokeRect } from './pixiGraphics.js';

let cellDictionary = Object.create(null);
export var renderArea = {
    x: 0,
    y: 0,
    width: 400,
    height: 400
};
export var cellSize = {
    x: 400,
    y: 400
};
export const marginCells = 1;

let camera;
let container;
let updateTicks = 0;
let debugGraphics;
let visibleCells = Object.create(null);
const registeredGraphics = new Set();
const pendingGraphics = new Set();
const settingsIndexCount = 2;
let debug = false;
let enabled = true;

const shouldCullGraphic = (graphic) => (
    graphic
    && !graphic.destroyed
    && !graphic.ignoreCulling
    && graphic !== debugGraphics
);

const setGraphicCulled = (graphic) => {
    if (!graphic || graphic.destroyed) return;

    // Pixi 8 has a dedicated culling flag. Keeping renderable untouched avoids
    // overwriting gameplay/editor visibility while still skipping the subtree.
    graphic.culled = graphic._cullingVisibleCells <= 0;
    if (graphic.forceRenderable !== undefined && !graphic.forceRenderable) {
        graphic.renderable = false;
    }

    // Pixi includes culling/renderability changes in its container change
    // tick. They do not move the graphic, so keep them from looking like a
    // dirty local transform on the next frame.
    graphic._cullingTransformTick = graphic._didContainerChangeTick;
};

const clearGraphicCells = (graphic) => {
    if (!graphic?._cullingCells) return;

    graphic._cullingCells.forEach((cell) => {
        const cellContents = cellDictionary[cell];
        if (!cellContents) return;

        const index = cellContents.indexOf(graphic, settingsIndexCount);
        if (index >= settingsIndexCount) cellContents.splice(index, 1);
        if (cellContents.length === settingsIndexCount && !cellContents[0]) {
            delete cellDictionary[cell];
        }
    });

    graphic._cullingVisibleCells = 0;
    graphic._cullingCells = [];
};

const cacheLocalTransform = (graphic) => {
    graphic.updateLocalTransform();
    graphic._cullingTransformTick = graphic._didContainerChangeTick;
    const transform = graphic.localTransform;
    let cached = graphic._cullingLocalTransform;

    if (!cached) {
        cached = graphic._cullingLocalTransform = {};
    }

    cached.a = transform.a;
    cached.b = transform.b;
    cached.c = transform.c;
    cached.d = transform.d;
    cached.tx = transform.tx;
    cached.ty = transform.ty;
};

const localTransformChanged = (graphic) => {
    if (graphic._cullingTransformTick === graphic._didContainerChangeTick) return false;
    graphic.updateLocalTransform();
    const transform = graphic.localTransform;
    const cached = graphic._cullingLocalTransform;

    return !cached
        || cached.a !== transform.a
        || cached.b !== transform.b
        || cached.c !== transform.c
        || cached.d !== transform.d
        || cached.tx !== transform.tx
        || cached.ty !== transform.ty;
};

const initGraphicForCulling = (graphic) => {
    if (!shouldCullGraphic(graphic)) return false;

    registeredGraphics.add(graphic);
    graphic._cullingSizeDirty = true;
    graphic._cullingCells = [];
    graphic._cullingVisibleCells = 0;
    graphic._cullings = 0;
    graphic._cullingLocalTransform = undefined;
    graphic._cullingTransformTick = undefined;
    graphic._cullingBoundsViewTick = undefined;

    return true;
};

export const getSizeInfoForGraphic = function (graphic) {
    const bounds = graphic.getLocalBounds();

    // Pixi reuses its Bounds instance, so retain an independent snapshot.
    graphic._cachedBounds = {
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height,
    };
    graphic._cullingBoundsViewTick = graphic._didViewChangeTick;
    graphic._cullingSizeDirty = false;
};

const placeGraphicInCells = function (graphic) {
    if (!enabled || !shouldCullGraphic(graphic)) return;
    if (!registeredGraphics.has(graphic) && !initGraphicForCulling(graphic)) return;

    clearGraphicCells(graphic);
    cacheLocalTransform(graphic);

    if (
        graphic._cullingSizeDirty
        || !graphic._cachedBounds
        || graphic._cullingBoundsViewTick !== graphic._didViewChangeTick
    ) {
        getSizeInfoForGraphic(graphic);
    }

    const bounds = graphic._cachedBounds;
    const transform = graphic.localTransform;
    const left = bounds.x;
    const top = bounds.y;
    const right = bounds.x + bounds.width;
    const bottom = bounds.y + bounds.height;

    const x1 = transform.a * left + transform.c * top + transform.tx;
    const y1 = transform.b * left + transform.d * top + transform.ty;
    const x2 = transform.a * right + transform.c * top + transform.tx;
    const y2 = transform.b * right + transform.d * top + transform.ty;
    const x3 = transform.a * right + transform.c * bottom + transform.tx;
    const y3 = transform.b * right + transform.d * bottom + transform.ty;
    const x4 = transform.a * left + transform.c * bottom + transform.tx;
    const y4 = transform.b * left + transform.d * bottom + transform.ty;

    const minX = Math.min(x1, x2, x3, x4);
    const minY = Math.min(y1, y2, y3, y4);
    const maxX = Math.max(x1, x2, x3, x4);
    const maxY = Math.max(y1, y2, y3, y4);

    if (
        cellSize.x <= 0
        || cellSize.y <= 0
        || ![minX, minY, maxX, maxY, cellSize.x, cellSize.y].every(Number.isFinite)
    ) {
        graphic.culled = false;
        return;
    }

    const startX = Math.floor(minX / cellSize.x);
    const startY = Math.floor(minY / cellSize.y);
    const endX = Math.floor(maxX / cellSize.x);
    const endY = Math.floor(maxY / cellSize.y);

    // Keep these legacy fields available for editor/debug code.
    graphic._cullingWidthExtent = (maxX - minX) / 2;
    graphic._cullingHeightExtent = (maxY - minY) / 2;
    graphic._cullingXTiles = endX - startX + 1;
    graphic._cullingYTiles = endY - startY + 1;

    for (let cellX = startX; cellX <= endX; cellX++) {
        for (let cellY = startY; cellY <= endY; cellY++) {
            const cell = `${cellX}_${cellY}`;

            if (cellDictionary[cell] === undefined) {
                cellDictionary[cell] = [false, 0];
            }
            cellDictionary[cell].push(graphic);

            if (cellDictionary[cell][0]) graphic._cullingVisibleCells++;
            graphic._cullingCells.push(cell);
        }
    }

    setGraphicCulled(graphic);
};

export const removeGraphicFromCells = function (graphic) {
    if (!graphic) return;

    pendingGraphics.delete(graphic);
    registeredGraphics.delete(graphic);
    clearGraphicCells(graphic);

    if (!graphic.destroyed) graphic.culled = false;
    graphic._cullingCells = undefined;
    graphic._cullingVisibleCells = 0;
    graphic._cullingLocalTransform = undefined;
    graphic._cullingTransformTick = undefined;
    graphic._cullingBoundsViewTick = undefined;
};

const queueGraphicForCulling = (graphic) => {
    if (!enabled || !shouldCullGraphic(graphic)) return;
    pendingGraphics.add(graphic);
};

const onChildAdded = (graphic, parent) => {
    if (parent === container) queueGraphicForCulling(graphic);
};

const onChildRemoved = (graphic, parent) => {
    if (parent === container) removeGraphicFromCells(graphic);
};

const processPendingGraphics = () => {
    if (!pendingGraphics.size) return;

    const graphics = Array.from(pendingGraphics);
    pendingGraphics.clear();

    graphics.forEach((graphic) => {
        if (graphic.parent === container && shouldCullGraphic(graphic)) {
            placeGraphicInCells(graphic);
        }
    });
};

const updateRegisteredGraphics = () => {
    for (const graphic of registeredGraphics) {
        if (
            graphic.destroyed
            || graphic.parent !== container
            || graphic.ignoreCulling
        ) {
            removeGraphicFromCells(graphic);
            continue;
        }

        if (
            graphic._cullingSizeDirty
            || graphic._cullingBoundsViewTick !== graphic._didViewChangeTick
            || localTransformChanged(graphic)
        ) {
            placeGraphicInCells(graphic);
        }
    }
};

const detachContainerListeners = () => {
    if (!container) return;
    container.off('childAdded', onChildAdded);
    container.off('childRemoved', onChildRemoved);
};

const resetRegisteredGraphics = () => {
    for (const graphic of registeredGraphics) {
        removeGraphicFromCells(graphic);
    }
    registeredGraphics.clear();
    pendingGraphics.clear();
};

const ensureDebugGraphics = () => {
    if (debugGraphics || !container) return;

    debugGraphics = new PIXI.Graphics();
    debugGraphics.ignoreCulling = true;
    container.addChild(debugGraphics);
};

export const toggleDebug = () => {
    setDebug(!debug);
};

export const setDebug = (bool) => {
    debug = bool;
    if (debug) ensureDebugGraphics();
    if (debugGraphics) debugGraphics.clear();
};

export const toggleEnabled = () => {
    setEnabled(!enabled);
};

export const setEnabled = (bool) => {
    if (enabled === bool) return;

    enabled = bool;
    if (!container) return;

    if (!enabled) {
        resetRegisteredGraphics();
        return;
    }

    container.children.forEach(queueGraphicForCulling);
    update();
};

export const disableCulling = function (sprite) {
    if (!sprite) return;

    sprite.ignoreCulling = true;
    removeGraphicFromCells(sprite);
    if (sprite.destroyed) return;

    sprite.renderable = true;
    if (sprite.forceRenderable !== undefined && !sprite.forceRenderable) {
        sprite.renderable = false;
    }
};

export const init = function (_container, _camera) {
    detachContainerListeners();
    resetRegisteredGraphics();

    if (debugGraphics && !debugGraphics.destroyed) {
        debugGraphics.destroy();
    }
    debugGraphics = undefined;

    container = _container;
    camera = _camera;
    cellDictionary = Object.create(null);
    updateTicks = 0;
    visibleCells = Object.create(null);

    if (!container) return;

    container.on('childAdded', onChildAdded);
    container.on('childRemoved', onChildRemoved);
    container.children.forEach(queueGraphicForCulling);

    if (debug) ensureDebugGraphics();
    update();
};

const isDescendantOf = (displayObject, ancestor) => {
    for (let current = displayObject; current; current = current.parent) {
        if (current === ancestor) return true;
    }
    return false;
};

export const getVisibleCellRange = function (
    targetContainer,
    targetCamera,
    area = renderArea,
    size = cellSize,
) {
    const globalStart = new PIXI.Point(area.x, area.y);
    const globalEnd = new PIXI.Point(area.x + area.width, area.y + area.height);
    const start = targetContainer.toLocal(globalStart);
    const end = targetContainer.toLocal(globalEnd);

    // The Pixi 8 camera owns the level container, so toLocal() has already
    // removed its transform. Keep the explicit inverse only for the detached
    // container arrangement used by the old projection-based camera.
    if (targetCamera && !isDescendantOf(targetContainer, targetCamera)) {
        targetCamera.matrix.applyInverse(start, start);
        targetCamera.matrix.applyInverse(end, end);
    }

    if (size.x <= 0 || size.y <= 0 || ![
        start.x, start.y, end.x, end.y,
        area.width, area.height, size.x, size.y,
    ].every(Number.isFinite)) return null;

    const left = Math.min(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);

    return {
        start: new PIXI.Point(left, top),
        width,
        height,
        startTileX: Math.floor(left / size.x),
        startTileY: Math.floor(top / size.y),
        visibleXTiles: Math.ceil(width / size.x) + 1,
        visibleYTiles: Math.ceil(height / size.y) + 1,
    };
};

const updateVisibleCells = function () {
    updateTicks++;
    const range = getVisibleCellRange(container, camera);
    if (!range) return false;

    const {
        start: sp,
        width: w,
        height: h,
        startTileX,
        startTileY,
        visibleXTiles,
        visibleYTiles,
    } = range;

    if (debug) {
        strokeRect(debugGraphics, sp.x, sp.y, w, h, 10, 0x00FF00);
    }

    for (let i = -marginCells; i < visibleXTiles + marginCells; i++) {
        for (let j = -marginCells; j < visibleYTiles + marginCells; j++) {
            const tileX = startTileX + i;
            const tileY = startTileY + j;
            const cell = `${tileX}_${tileY}`;

            if (cellDictionary[cell] === undefined) {
                cellDictionary[cell] = [true, updateTicks];
            } else {
                cellDictionary[cell][1] = updateTicks;
            }
            visibleCells[cell] = cell;

            if (debug) {
                strokeRect(
                    debugGraphics,
                    tileX * cellSize.x,
                    tileY * cellSize.y,
                    cellSize.x,
                    cellSize.y,
                    10,
                    0x008800,
                    0.5,
                );
            }
        }
    }

    return true;
};

const setGraphicsVisible = function (arr) {
    for (let i = settingsIndexCount; i < arr.length; i++) {
        setGraphicCulled(arr[i]);
    }
};

const updateCells = function () {
    for (const cell in visibleCells) {
        const cellContents = cellDictionary[cell];
        if (!cellContents) {
            delete visibleCells[cell];
            continue;
        }

        if (cellContents[1] !== updateTicks) {
            cellContents[0] = false;
            for (let i = settingsIndexCount; i < cellContents.length; i++) {
                cellContents[i]._cullingVisibleCells--;
            }
            setGraphicsVisible(cellContents);

            if (cellContents.length === settingsIndexCount) {
                delete cellDictionary[cell];
            }
            delete visibleCells[cell];
        } else if (cellContents[0] === false) {
            cellContents[0] = true;
            for (let i = settingsIndexCount; i < cellContents.length; i++) {
                cellContents[i]._cullingVisibleCells++;
            }
            setGraphicsVisible(cellContents);
        }
    }
};

const drawAllCells = function () {
    setStrokeStyle(debugGraphics, 10, 0xFF0000, 0.5);

    for (const cell in cellDictionary) {
        const splitCellString = cell.split('_');
        const tileX = parseInt(splitCellString[0]);
        const tileY = parseInt(splitCellString[1]);
        debugGraphics.rect(
            tileX * cellSize.x,
            tileY * cellSize.y,
            cellSize.x,
            cellSize.y,
        );
    }
    debugGraphics.stroke();
};

export const update = function () {
    if (!enabled || !container) return;

    processPendingGraphics();
    updateRegisteredGraphics();

    if (debug) {
        ensureDebugGraphics();
        debugGraphics.clear();
        drawAllCells();
    }

    if (updateVisibleCells()) updateCells();
};
